import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { userTypesQueryOptions } from '@/features/users/api/userTypes.api';
import { useRoleForm } from '../hooks/useRoleForm';
import type { Role } from '../types/role.types';

interface RoleFormModalProps {
  role: Role | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RoleFormModal({ role, open, onOpenChange }: RoleFormModalProps) {
  const {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  } = useRoleForm({ role, open, onClose: () => onOpenChange(false) });

  const { data: userTypes, isLoading: userTypesLoading } = useQuery({
    ...userTypesQueryOptions(),
    enabled: open,
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  }

  const title = isEditMode ? 'Edit Role' : 'Create Role';
  const description = isEditMode
    ? `Update details for "${role?.name}".`
    : 'Add a role that users can be assigned to.';

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Area Manager" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Details</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        placeholder="What this role is for"
                        rows={3}
                        className="min-h-20 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring/30 resize-y"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="userTypeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>User Type</FormLabel>
                    <FormControl>
                      <OptionSelect
                        id="role-user-type"
                        options={userTypes}
                        isLoading={userTypesLoading}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select user type"
                        clearable
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={field.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange(true)}
                        >
                          Active
                        </Button>
                        <Button
                          type="button"
                          variant={!field.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange(false)}
                        >
                          Inactive
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={requestClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : isEditMode ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && cancelDiscard()}
        onDiscard={confirmDiscard}
      />
    </>
  );
}
